// In @/components/ui/FormField.tsx (or wherever FormField is defined)
import { Control, Controller } from 'react-hook-form';
import { ReactNode, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { fontSize, fontWeight, textColor, borderColor, focusStyle } from '@/theme';

interface FormFieldProps {
  control: Control<any>;
  name: string;
  label?: string;
  placeholder?: string;
  type?: string;
  icon?: ReactNode;
  maxLength?: number;
  isSelect?: boolean;
  options?: Array<{ value: string; label: string }>;
  isTextarea?: boolean;
  rows?: number;
  onClear?: () => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

export const FormField = ({
  control,
  name,
  label,
  placeholder,
  type = 'text',
  icon,
  maxLength,
  isSelect,
  options,
  isTextarea,
  rows = 3,
  onClear,
  error: externalError,
  disabled = false,
  required = false,
}: FormFieldProps) => {
  if (isSelect) {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState: { error } }) => (
          <div>
            {label && (
              <label className={`block ${fontSize.lg} ${fontWeight.bold} mb-1`}>
                {label} {required && <span className={textColor.error}>*</span>}
              </label>
            )}
            <select
              {...field}
              disabled={disabled}
              onChange={(e) => {
                field.onChange(e);
                onClear?.();
              }}
              className={`w-full p-3 border rounded-lg ${focusStyle.primary}`}
            >
              {options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {(error || externalError) && (
              <p className={`${textColor.error} ${fontSize.sm} mt-1`}>{error?.message || externalError}</p>
            )}
          </div>
        )}
      />
    );
  }

  if (isTextarea) {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState: { error } }) => (
          <div>
            {label && (
              <label className={`block ${fontSize.md} ${fontWeight.bold} mb-1`}>
                {label} {required && <span className={textColor.error}>*</span>}
              </label>
            )}
            <textarea
              {...field}
              rows={rows}
              placeholder={placeholder}
              disabled={disabled}
              onChange={(e) => {
                field.onChange(e);
                onClear?.();
              }}
              className={`w-full p-3 border rounded-lg ${focusStyle.primary} resize-vertical`}
            />
            {(error || externalError) && (
              <p className={`${textColor.error} ${fontSize.sm} mt-1`}>{error?.message || externalError}</p>
            )}
          </div>
        )}
      />
    );
  }

  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <div>
          {label && (
            <label className={`block ${fontSize.md} ${fontWeight.bold} mb-1`}>
              {label} {required && <span className={textColor.error}>*</span>}
            </label>
          )}
          <div className="relative">
            {icon && (
              <div className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${textColor.icon} flex items-center`}>
                {icon}
              </div>
            )}
            <input
              {...field}
              type={inputType}
              placeholder={placeholder}
              maxLength={maxLength}
              disabled={disabled}
              onChange={(e) => {
                field.onChange(e);
                onClear?.();
              }}
              className={`w-full p-3 ${icon ? 'pl-10' : ''} ${isPassword ? 'pr-10' : ''} border rounded-lg ${focusStyle.primary} ${
                error || externalError ? borderColor.error : ''
              }`}
            />
            {isPassword && (
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${textColor.icon} hover:text-gray-700`}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            )}
          </div>
          {(error || externalError) && (
            <p className={`${textColor.error} ${fontSize.sm} mt-1`}>{error?.message || externalError}</p>
          )}
        </div>
      )}
    />
  );
};
